import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { useTenant } from "@/hooks/useTenant";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useResolvedCheckedInTrailer, useSyncInheritedTrailerType, useCustomTrailerDefault, customTrailerDefaultSlugFor } from "@/components/orders/trailerTypeFieldLogic";

interface TrailerTypeFieldProps {
  /** The trailer_id string this type is associated with (used for check-in lookup). */
  trailerId: string;
  value: string;
  onChange: (typeId: string) => void;
  disabled?: boolean;
  /** When true, filters out bobtail types (per-leg pickers should not offer bobtail). */
  excludeBobtail?: boolean;
  /** Short code of the location this trailer departs from (the leg's Ship From). */
  originShortCode?: string | null;
  /** Saved trailer ID for this field; an unchanged trailer never gets defaulted. */
  baselineTrailerId?: string;
}

/**
 * Reusable Trailer Type picker. Mirrors the order-level behavior used by
 * Create/Edit dialogs:
 *  - Inherits from a matching checked-in trailer's type.
 *  - When the user chooses a different type than the inherited one, prompts
 *    to confirm and, on confirm, writes the new type back to the `trailers`
 *    row so the check-in record stays in sync.
 *  - Locally staged overrides are cleared if the trailer_id changes.
 *
 * Unlike the order-level flow (which stages the write to the mutation), this
 * field applies the trailers-row update immediately on confirm because
 * per-leg pickers can't rely on a single form-submit handler to fan out
 * writes.
 */
export function TrailerTypeField({
  trailerId,
  value,
  onChange,
  disabled,
  excludeBobtail,
  originShortCode,
  baselineTrailerId = "",
}: TrailerTypeFieldProps) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const { data: trailerTypes } = useTrailerTypes();
  const { matched, matchedTrailerRowId, inheritedTypeId, inheritedTypeLabel, isLoading: rosterLoading } =
    useResolvedCheckedInTrailer(trailerId);
  const defaultSlug = customTrailerDefaultSlugFor(originShortCode);
  const defaultTypeId =
    (defaultSlug && trailerTypes?.find((t) => t.slug === defaultSlug)?.id) || null;
  const showDefaultReminder = useCustomTrailerDefault({
    trailerId,
    baselineTrailerId,
    matched,
    rosterLoading,
    defaultTypeId,
    currentTypeId: value,
    setTypeId: onChange,
  });

  const [pendingTypeChange, setPendingTypeChange] = useState<string | null>(null);
  const [overrideActive, setOverrideActive] = useState(false);

  // Reset override tracking whenever the resolved trailer changes.
  useEffect(() => {
    setPendingTypeChange(null);
    setOverrideActive(false);
  }, [matchedTrailerRowId]);

  useSyncInheritedTrailerType(
    inheritedTypeId,
    matched,
    value,
    onChange,
    overrideActive,
  );

  const handleChange = (next: string) => {
    const resolved = next === "none" ? "" : next;
    if (matched && matchedTrailerRowId && resolved !== (inheritedTypeId ?? "")) {
      setPendingTypeChange(resolved);
      return;
    }
    onChange(resolved);
  };

  const confirmOverride = async () => {
    if (pendingTypeChange === null || !matchedTrailerRowId || !tenantId) {
      setPendingTypeChange(null);
      return;
    }
    const newTypeId = pendingTypeChange;
    const { error } = await supabase
      .from("trailers")
      .update({ trailer_type_id: newTypeId || null })
      .eq("id", matchedTrailerRowId)
      .eq("tenant_id", tenantId);
    if (error) {
      toast.error(`Failed to update check-in record: ${error.message}`);
      setPendingTypeChange(null);
      return;
    }
    setOverrideActive(true);
    onChange(newTypeId);
    queryClient.invalidateQueries({ queryKey: ["checked-in-trailers", tenantId] });
    queryClient.invalidateQueries({ queryKey: ["trailers"] });
    setPendingTypeChange(null);
  };

  const options = (trailerTypes ?? []).filter((t) =>
    excludeBobtail ? t.kind !== "bobtail" : true,
  );

  return (
    <>
      <Select value={value || "none"} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue placeholder="Select trailer type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None</SelectItem>
          {options.map((t) => (
            <SelectItem key={t.id} value={t.id}>
              {t.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {matched && inheritedTypeLabel && !overrideActive && (
        <p className="mt-1 text-xs text-muted-foreground">From checked-in trailer</p>
      )}
      {showDefaultReminder && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          Defaulted to {getTrailerTypeLabel(defaultTypeId, trailerTypes)} for {originShortCode} departures — please confirm this is the right trailer type.
        </p>
      )}
      {overrideActive && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          Check-in record updated
        </p>
      )}

      <AlertDialog
        open={pendingTypeChange !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTypeChange(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Override Checked-In Trailer Type?</AlertDialogTitle>
            <AlertDialogDescription>
              Trailer <strong>{trailerId}</strong> is checked in as{" "}
              <strong>{inheritedTypeLabel || "(no type)"}</strong>. Changing this
              will update the check-in record so the trailer's type stays in
              sync across the yard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOverride}>Update</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}